import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import apiClient from '../utils/apiClient';

export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    // Page load = READ ONLY. This used to run the full email-processing
    // pipeline first (POST /tickets/process: Freshdesk fetches, Brain
    // lookups, LLM calls for up to 10 tickets) before showing anything —
    // which is why the page took 30s+ to open. The background poller
    // handles new mail; the "Process New" button forces a pull on demand.
    fetchTickets();
  }, []);

  const fetchTickets = async (silent = false) => {
    const toastId = silent ? null : toast.loading('Loading tickets...');
    try {
      setLoading(true);
      const response = await apiClient.get('/api/v1/tickets/', {
        params: { limit: 50 },
      });
      setTickets(response.data);
      if (!silent) toast.success('Tickets loaded', { id: toastId });
    } catch (error) {
      console.error('Error fetching tickets:', error);
      if (!silent) toast.error('Failed to load tickets', { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  const processNewTickets = async () => {
    const toastId = toast.loading('Pulling and processing new tickets…');
    setProcessing(true);
    try {
      await apiClient.post('/api/v1/tickets/process', { limit: 10 });
      await fetchTickets(true);
      toast.success('New tickets processed', { id: toastId });
    } catch (error) {
      console.error('Error processing tickets:', error);
      toast.error('Failed to process new tickets', { id: toastId });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading tickets...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Tickets</h1>
          <p className="mt-2 text-sm text-gray-600">{tickets.length} total tickets</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => fetchTickets()}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Refresh
          </button>
          <button
            onClick={processNewTickets}
            disabled={processing}
            title="Pull open Freshdesk tickets and run them through Henry now (the background poller does this automatically)"
            className="px-4 py-2 border border-primary-600 text-primary-700 rounded-lg hover:bg-primary-50 disabled:opacity-50"
          >
            {processing ? 'Processing…' : 'Process New'}
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Customer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Subject
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Intent
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Created
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tickets.map((ticket) => (
              <tr key={ticket.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {ticket.freshdesk_ticket_id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-xs font-medium text-gray-900">
                    {ticket.customer_name || 'Unknown'}
                  </div>
                  <div className="text-xs text-gray-400">{ticket.customer_email}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-xs text-gray-900 truncate" title={ticket.subject}>{ticket.subject}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      ticket.status === 'processed'
                        ? 'bg-green-100 text-green-800'
                        : ticket.status === 'queued'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {ticket.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {ticket.classification?.intent || 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                  <div className="whitespace-nowrap">
                    {new Date(ticket.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} {new Date(ticket.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
